import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { OrdemCompraLista } from "../../../domain/models/ordem_compra_lista";
import { SendMailModal } from "../../../../../common/components/send_email_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { ReportOrdemComprasModal } from "./components/report_ordem_compras_modal";
import { ReportOrdemComprasLoteModal } from "./components/report_ordem_compras_lote_modal";
import { GenerateOrdemComprasEntregaColumnDefinition } from "./columns";
import { useOrdemCompraListaCancelarViewModel } from "../../viewModels/ordem_compras_lista_cancelar_view_model";

import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";

/**
 * OrdemCompraListaCancelar
 */

export const OrdemCompraListaCancelar = () => {
  const viewModel = useOrdemCompraListaCancelarViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateOrdemComprasEntregaColumnDefinition({
      onActionEditar: viewModel.handleOrdemCompraEditarSubmit,
      onActionExportar: viewModel.handleOrdemCompraReportSubmit,
      onActionEmail: viewModel.handleEmailOpenState,
    });
  }, [
    viewModel.handleOrdemCompraEditarSubmit,
    viewModel.handleOrdemCompraReportSubmit,
    viewModel.handleEmailOpenState,
  ]);

  return (
    <>
      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Compras
          </Link>
          <Typography className="text-primary-green">
            Cancelar Ordem de Compra
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Cancelar Ordem de Compra</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex gap-2">
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload}
                />
              </div>
            ) : (
              <>
                <Header
                  centroCusto={viewModel.filters.centroCusto}
                  fornecedor={viewModel.filters.fornecedor}
                  coletaPreco={viewModel.filters.coletaPreco}
                  ordemCompra={viewModel.filters.ordemCompra}
                  produtoServico={viewModel.filters.produtoServico}
                  status={viewModel.filters.status}
                  statusNF={viewModel.filters.statusNF}
                  nroNotaFiscal={viewModel.filters.nroNotaFiscal}
                  dataOCStart={viewModel.filters.dataOCStart}
                  dataOCEnd={viewModel.filters.dataOCEnd}
                  centroCustoDropdown={viewModel.centroCustoDropdown}
                  fornecedoresDropdown={viewModel.fornecedoresDropdown}
                  coletaPrecoDropdown={viewModel.coletaPrecosDropdown}
                  ordemComprasDropdown={viewModel.ordemComprasDropdown}
                  produtoServicosDropdown={viewModel.produtoServicosDropdown}
                  onCentroCustoChange={viewModel.handleCentroCustoChange}
                  onFornecedorChange={viewModel.handleFornecedorChange}
                  onColetaPrecoChange={viewModel.handleColetaPrecoChange}
                  onOrdemCompraChange={viewModel.handleOrdemCompraChange}
                  onProdutoServicoChange={viewModel.handleProdutoServicoChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onStatusNFChange={viewModel.handleStatusNFChange}
                  onNroNotaFiscalChange={viewModel.handleNroNotaFiscalChange}
                  onDataOCRChange={viewModel.handleDataOCRChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="coleta_precos_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    getRowId={(row: OrdemCompraLista) => row.idOrdemCompra}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.ordemCompras}
                    rowCount={viewModel.ordemCompras.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <SendMailModal
        open={viewModel.emailModal}
        loading={viewModel.loading}
        sendTo={viewModel.emailSendTo}
        subject={viewModel.emailSubject}
        attachment={viewModel.emailAttachment}
        body={viewModel.emailBody}
        onClose={() => viewModel.handleEmailOpenState(null, false)}
        onSubmit={viewModel.handleSendMailSubmit}
      />

      <ReportOrdemComprasModal
        open={viewModel.reportOrdemComprasModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        ordemComprasDropdown={viewModel.ordemComprasDropdown}
        centroCustosDropdown={viewModel.centroCustoDropdown}
        fornecedoresDropdown={viewModel.fornecedoresDropdown}
        onClose={() => viewModel.handleReportOrdemComprasOpenState(false)}
        onSubmit={viewModel.handleReportOrdemComprasSubmit}
      />

      <ReportOrdemComprasLoteModal
        open={viewModel.reportOrdemComprasLoteModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        centroCustosDropdown={viewModel.centroCustoDropdown}
        fornecedoresDropdown={viewModel.fornecedoresDropdown}
        onClose={() => viewModel.handleReportOrdemComprasLoteOpenState(false)}
        onSubmit={viewModel.handleReportOrdemComprasLoteSubmit}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

    </>
  );
}